import './assets/main.css'
import 'primeicons/primeicons.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import da from 'primelocale/da.json'
import en from 'primelocale/en.json'
import i18n from '@/utilities/I18n'

import App from './App.vue'

import WakeUpDetector from '@/utilities/WakeUpDetector'
import globalEvents from '@/utilities/GlobalEvents'

import { useSettingsStore } from '@/stores/Settings'
import { useThemeStore } from '@/stores/Theme'
import { useApiStore } from '@/stores/Api'
import { useRealtimeStore } from '@/stores/Realtime'
import { useEmployeesStore } from '@/stores/Employees'
import { useQueuesStore } from '@/stores/Queues'
import { usePhonebookStore } from '@/stores/Phonebook'
import { useDashboardsStore } from '@/stores/Dashboards'
import { useVoicemailStore } from '@/stores/Voicemails'
import { useCallerIdNumbersStore } from '@/stores/CallerIdNumbers'
import { useElectronStore } from '@/stores/Electron'
import { useMyCallsStore } from '@/stores/MyCalls'
import CallDrag from '@/utilities/CallDrag'
import SipClient from '@/utilities/SipClient'
import ConfirmationService from 'primevue/confirmationservice'
import logger from '@/utilities/Logger'

if (!window.location.protocol.startsWith('https')) {
	window.location.href = 'https://' + window.location.hostname
}

const app = createApp(App)

app.use(createPinia())
app.use(ConfirmationService)

// app.use(router)

const MyPreset = definePreset(Aura, {
	semantic: {
		primary: {
			50: '#F9EAD7',
			100: '#F1E2CF',
			200: '#E9DAC7',
			300: '#E1D2BF',
			400: '#D9CAB7',
			500: '#AC9D8B',
			600: '#807362',
			700: '#574B3B',
			800: '#44392A',
			900: '#312719',
			950: '#211709'
		},
		colorScheme: {
			light: {
				surface: {
					50: '{stone.50}',
					100: '{stone.100}',
					200: '{stone.200}',
					300: '{stone.300}',
					400: '{stone.400}',
					500: '{stone.500}',
					600: '{stone.600}',
					700: '{stone.700}',
					800: '{stone.800}',
					900: '{stone.900}',
					950: '{stone.950}'
				},
				primary: {
					color: '{primary.500}',
					contrastColor: '#ffffff',
					hoverColor: '{primary.600}',
					activeColor: '{primary.700}'
				},
				highlight: {
					background: '{primary.50}',
					focusBackground: '{primary.100}',
					color: '{primary.700}',
					focusColor: '{primary.800}'
				},
				mask: {
					background: 'rgba(0,0,0,0.4)',
					color: '{surface.200}'
				}
			},
			dark: {
				surface: {
					50: '{stone.50}',
					100: '{stone.100}',
					200: '{stone.200}',
					300: '{stone.300}',
					400: '{stone.400}',
					500: '{stone.500}',
					600: '{stone.600}',
					700: '{stone.700}',
					800: '{stone.800}',
					900: '{stone.900}',
					950: '{stone.950}'
				},
				primary: {
					color: '{primary.400}',
					contrastColor: '{surface.900}',
					hoverColor: '{primary.300}',
					activeColor: '{primary.200}'
				},
				highlight: {
					background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
					focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
					color: 'rgba(255,255,255,.87)',
					focusColor: 'rgba(255,255,255,.87)'
				},
				mask: {
					background: 'rgba(0,0,0,0.6)',
					color: '{surface.200}'
				},
			}
		}
	},
	components: {
		button: {
			colorScheme: {
				light: {
					root: {
						secondary: {
							background: '{zinc.200}',
							borderColor: '{zinc.200}',
							hoverBackground: '{zinc.300}',
							hoverBorderColor: '{zinc.100}'
						},
						primary: {
							background: '{zinc.600}',
							hoverBackground: '{zinc.700}',
							borderColor: '{zinc.600}',
							hoverBorderColor: '{zinc.700}',
						}
					}
				},
				dark: {
					root: {
						secondary: {
							background: '{zinc.700}',
							borderColor: '{zinc.700}',
						},
						primary: {
							background: '{primary.500}',
							borderColor: '{primary.600}',
							hoverBackground: '{primary.400}',
							hoverBorderColor: '{primary.500}'
						}
					}
				}
			}
		},
		toggleswitch: {
			colorScheme: {
				light: {
					root: {
						checkedBackground: '{lime.500}',
						checkedHoverBackground: '{lime.600}',
						background: '{surface.400}',
						height: '18px'
					}
				},
				dark: {
					root: {
						checkedBackground: '{green.400}',
						checkedHoverBackground: '{green.300}'
					}
				}
			}
		}
	}
})

// Extend locale configurations with firstDayOfWeek
const daLocale = { ...da.da, firstDayOfWeek: 1 } // Monday for Denmark
const enLocale = { ...en.en, firstDayOfWeek: 0 } // Sunday for English

app.use(PrimeVue, {
	theme: {
		preset: MyPreset,
		options: {
			darkModeSelector: 'system',
			// cssLayer: {
			// 	name: 'primevue',
			// 	order: 'tailwind-base, primevue, tailwind-utilities'
			// }
		}
	},
	ripple: true,
	locale: navigator.language.split('-')[0] == 'da' ? daLocale : enLocale
})
app.use(i18n)

useApiStore()
const ss = useSettingsStore()

await ss.mainInit()

const realtime = useRealtimeStore()

await realtime.init()

useThemeStore()
useEmployeesStore()
useQueuesStore()
usePhonebookStore()
useDashboardsStore()
useVoicemailStore()
useCallerIdNumbersStore()
useElectronStore()
useMyCallsStore()

CallDrag.create()
SipClient.create()

app.mount('#app')

WakeUpDetector.create(() => {
	logger.debug('Settings: Wakeup occured')
	if (!ss.isDevelopment) {
		globalEvents.emit('Wakeup')
		ss.loadAuth() // Will trigger everyting else to also refresh
	} else {
		logger.info('Settings: Wakeup occured - pretending to refresh all')
	}
})
